import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import Image from 'next/image';
import type { ResolvedOffer } from './types';
import { Button } from '@/components/Button';
import { EditInStrapiBadge } from '@/components/preview/EditInStrapiBadge';
import { PromoBadge } from '@/components/PromoBadge';
import type { EventTrackingResolved } from '@/lib/gtm/pushCmsEventTracking';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { headingTag, type HeadingLevel } from '@/lib/render/headingTag';
import { imageAlt } from '@/lib/render/imageAlt';
import { RichText } from '@/lib/render/RichText';
import { substituteHref } from '@/lib/render/substituteHref';

interface BigOfferCardProps {
  offer: ResolvedOffer;
  sub: (s: string) => string;
  imageUrl: (img: { url?: string } | undefined) => string;
  phone?: string;
  matelsoPhoneLabel?: string;
  phoneTracking?: EventTrackingResolved[];
  /** Level of the offer title; `2` when unset (70.15-Heading-Structure). */
  titlePower?: HeadingLevel;
  visualOnly?: boolean;
}

export function BigOfferCard({
  offer,
  sub,
  imageUrl,
  phone,
  matelsoPhoneLabel,
  phoneTracking,
  titlePower = '2',
  visualOnly,
}: BigOfferCardProps) {
  const imgSrc = offer.image ? imageUrl(offer.image) : null;
  // The offer's title is a stand-in name for the picture; the media's own alt
  // text describes it and wins when an editor has filled it in.
  const badgeParts = (offer.badgeText ? sub(offer.badgeText) : '').split('\n');
  const ctaHref = substituteHref(offer.ctaLink?.href, sub);
  const { as: Title, className: titleClass } = headingTag(
    titlePower,
    visualOnly,
  );

  return (
    <div>
      {offer.categoryLabel && (
        <p className="h6 text-primary mb-4 text-center text-base font-bold tracking-[0.05em] uppercase">
          {sub(offer.categoryLabel)}
        </p>
      )}
      {offer.title && (
        <Title className={classNames(titleClass, 'mb-4 text-center md:mb-10')}>
          {sub(offer.title)}
        </Title>
      )}
      <div className="group/offer relative flex flex-col gap-6 md:flex-row md:items-start md:gap-8">
        {offer.documentId && (
          <div className="pointer-events-none absolute top-2 left-2 z-20 opacity-0 transition-opacity group-hover/offer:opacity-100">
            <div className="pointer-events-auto">
              <EditInStrapiBadge
                target={{
                  collection: 'api::offer.offer',
                  documentId: offer.documentId,
                }}
              />
            </div>
          </div>
        )}
        {imgSrc && (
          <div className="relative shrink-0 md:w-4/7">
            <div className="relative aspect-video overflow-hidden rounded-2xl md:ml-12">
              <Image
                src={imgSrc}
                alt={sub(imageAlt(offer.image, offer.title ?? ''))}
                fill
                // Full padded width when stacked; from md up, 4/7 of the
                // widget's max-w-6xl row (1080px inside lg:px-9) minus this
                // box's md:ml-12 — 570px at most, and below 57vw before that.
                sizes="(max-width: 767px) calc(100vw - 2rem), min(57vw, 570px)"
                className="object-cover"
              />
            </div>
            {offer.badgeText && (
              <PromoBadge
                prefix={badgeParts[0] ?? ''}
                price={badgeParts[1] ?? ''}
                unit={badgeParts[2] ?? ''}
                className="absolute -bottom-5 -left-2 z-10 -rotate-10 md:left-4"
                variant="offer"
              />
            )}
          </div>
        )}
        <div className="py-0 text-left md:w-3/8 md:py-4">
          {offer.subTitle && (
            <p className="mb-2 text-base leading-7 font-medium md:m-auto md:text-lg">
              {sub(offer.subTitle)}
            </p>
          )}
          {offer.benefits && (
            <div className="benefits-checkmarks mt-2 text-sm md:text-base">
              <RichText nodes={offer.benefits} />
            </div>
          )}
          <div className="mt-6 flex flex-col gap-3">
            {phone && (
              <Button
                href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                variant="primary"
                onClick={() =>
                  pushCmsEventTracking(phoneTracking, {
                    label: matelsoPhoneLabel || phone,
                    phone: matelsoPhoneLabel || phone,
                  })
                }
                className="text-sm md:py-3! md:text-lg/5!"
                aria-label={`Anrufen: ${matelsoPhoneLabel || phone}`}
              >
                {matelsoPhoneLabel || phone}
              </Button>
            )}
            {offer.ctaButtonText && ctaHref && (
              <Button
                href={ctaHref}
                variant="secondary"
                className="text-sm/4 md:py-3! md:text-lg/5!"
                target={offer.ctaLink?.target}
              >
                {sub(offer.ctaButtonText)}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
